import { Component, OnInit, ViewChild, TemplateRef, ElementRef } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { ActivatedRoute, NavigationExtras, Router, RouterModule } from '@angular/router';
import { OrderService } from '../../services/order.service';
import { OrderDetailDto, ParcelDto, RepackCancelRequestDto, FrontendRepackCancelParcelAction, SenderInfoDto, ReceiverInfoDto, ParcelSenderInfoDto, ParcelReceiverInfoDto } from '../../../models/order.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { RepackCancelModalComponent, RepackCancelChoice } from '../modals/repack-cancel-modal/repack-cancel-modal.component';
import { RepackingStateService, PendingRepackState, ReusableCartonConfiguration } from '../../services/repacking-state.service';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft, lucidePlusCircle, lucidePencil, lucideXCircle,
  lucideClipboardCheck, lucidePackage, lucideFileText
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
  EmptyStateComponent, SkeletonComponent
} from '@shared/ui/components';

@Component({
  selector: 'app-order-detail',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule, DatePipe,
    ...HlmCardImports, HlmButton,
    NgIcon, HlmIcon,
    PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
    EmptyStateComponent, SkeletonComponent,
  ],
  providers: [provideIcons({
    lucideArrowLeft, lucidePlusCircle, lucidePencil, lucideXCircle,
    lucideClipboardCheck, lucidePackage, lucideFileText
  })],
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.css']
})
export class OrderDetailComponent implements OnInit {
  order: OrderDetailDto | null = null;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null; // For success feedback
  orderId: string | null = null;

  // For repack operations
  repackCancelReasonCode: number = 1;
  selectedParcelsForUnbind: ParcelDto[] = [];

  @ViewChild('repackCreateConfirmModal') repackCreateConfirmModal: any;
  @ViewChild('repackUpdateOptionsModal') repackUpdateOptionsModal: any;
  @ViewChild('repackUpdateModal') repackUpdateModal: any;
  @ViewChild('repackCancelModal') repackCancelModal: any;
  @ViewChild('customsClearanceModal') customsClearanceModal: any;

  // Customs Clearance modal properties
  selectedParcelsForCC: ParcelDto[] = [];
  filteredParcelsForCC: ParcelDto[] = [];
  parcelFilterText: string = '';


  constructor(
    private route: ActivatedRoute,
    private orderService: OrderService,
    private location: Location, // Inject Location to go back
    private router: Router, // For programmatic navigation if needed
    // private modalService: NgbModal, // Removed for ng-zorro-antd
    private repackingStateService: RepackingStateService
  ) {}

  ngOnInit(): void {
    this.orderId = this.route.snapshot.paramMap.get('id');
    if (this.orderId) {
      this.loadOrderDetails();
    } else {
      this.errorMessage = 'Order ID not found in route.';
      this.isLoading = false;
    }
  }

  loadOrderDetails(): void {
    if (!this.orderId) return;
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.orderService.getOrderDetails(this.orderId).subscribe({
      next: (data) => {
        this.order = data;
        this.isLoading = false;
        console.log("Data from the order: ", JSON.stringify(this.order, null, 2));
      },
      error: (err: HttpErrorResponse) => {
        this.handleApiError(err, `Failed to load order details for ID ${this.orderId}`);
      }
    });
  }

  goBack(): void {
    this.location.back(); // Navigates to the previous page in history
  }
  // Placeholder for Repack Create (might be moved to a separate component/modal)
  initiateRepackCreate(): void {
    if (!this.order) return;
    
    // TODO: Replace with ng-zorro-antd modal when available
    console.log('Repack create initiated for order:', this.order.shopeeOrderNo);
    const confirmed = confirm('Are you sure you want to create a new repack?');
    if (confirmed) {
      this.performRepackCreate();
    }
  }

    private performRepackCreate(): void {
    if (!this.order) return;
    
    const cartonId = this.order.cartonNo || this.order.carrierTrackingNumber || '';
    const shopeeNo = this.order.shopeeOrderNo;
    
    if (!cartonId && !shopeeNo) {
      this.errorMessage = "Cannot identify the carton for cancellation.";
      return;
    }
    
    this.isLoading = true;
    this.errorMessage = null;
      const cancelRequestDto: RepackCancelRequestDto = {
      repackedCartonNo: cartonId,
      ilhShopeeNo: shopeeNo,
      destinationRegion: this.order?.destinationRegion || '',
      reasonCode: '1', // Default reason code
      parcelAction: FrontendRepackCancelParcelAction.MoveToNewCarton // Explicitly indicate intent to create new carton
    };
    
    this.orderService.repackCancel(cancelRequestDto).subscribe({
      next: (response: any) => {
        this.isLoading = false;        this.successMessage = `Carton cancelled successfully. Preparing to create new carton...`;
        
        // Prepare carrier information
        let preCarrierToUse: any = null;
        if ((this.order as any)?.preCarrierinfo) {
          preCarrierToUse = Array.isArray((this.order as any).preCarrierinfo) ? 
            { ...((this.order as any).preCarrierinfo[0]) } : 
            { ...((this.order as any).preCarrierinfo) };
        }
        
        let nextCarrierToUse: any = null;
        if ((this.order as any)?.nextCarrierinfo) {
          nextCarrierToUse = Array.isArray((this.order as any).nextCarrierinfo) ?
            { ...((this.order as any).nextCarrierinfo[0]) } :
            { ...((this.order as any).nextCarrierinfo) };
        }
          // Only keep essential information for the new carton
        // We'll include the physical dimensions for reference, but user will input new values
        const cartonConfigToReuse = {
          destinationRegion: this.order?.destinationRegion || '',
          serviceCode: this.order?.serviceCode ?? undefined,
          goodsType: this.order?.goodsType !== undefined ? Number(this.order.goodsType) : undefined,
          transportType: this.order?.transportType !== undefined ? Number(this.order.transportType) : undefined,
          
          // Include carton physical details for reference
          cartonWeight: this.order?.cartonWeight,
          cartonLength: this.order?.cartonLength,
          cartonWidth: this.order?.cartonWidth,
          cartonHeight: this.order?.cartonHeight,
          cartonVolume: this.order?.cartonVolume
        };
        
        // Set up state for repack create
        const pendingState: PendingRepackState = {
          parcelsForRepack: this.order?.parcels || [],
          sourceOrderUniqueId: this.order?.uniqueId || this.orderId!,
          cancelledCartonIlhShopeeNo: shopeeNo,
          senderToUse: this.order?.Sender ? { ...this.order.Sender } : undefined,
          receiverToUse: this.order?.Receiver ? { ...this.order.Receiver } : undefined,
          preCarrierToUse: preCarrierToUse,
          nextCarrierToUse: nextCarrierToUse,
          cartonConfigToReuse: cartonConfigToReuse
        };
          console.log("Setting PendingRepackState for create:", JSON.stringify(pendingState, null, 2));
        this.repackingStateService.setPendingRepackState(pendingState);
        
        // Navigate to repack create
        setTimeout(() => {
          this.navigateToRepackCreate();
        }, 1000);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        if (err.error && err.error.message) {
          this.errorMessage = `Failed to cancel carton: ${err.error.message}`;
        } else if (err.message) {
          this.errorMessage = `Failed to cancel carton: ${err.message}`;
        } else {
          this.errorMessage = 'An unknown error occurred during carton cancellation.';
        }
        console.error('Repack cancel error:', err);
      }
    });
  }

  // Placeholder for Repack Update
  initiateRepackUpdate(): void {
    if (!this.order) return;
    
    // Reset selected parcels
    this.selectedParcelsForUnbind = [];
    
    // TODO: Replace with ng-zorro-antd modal when available
    console.log('Repack update initiated for order:', this.order.shopeeOrderNo);
    const action = confirm('Add parcels (OK) or Remove parcels (Cancel)?');
    if (action) {
      this.navigateToAddParcels();
    } else {
      this.showParcelSelectionForUnbinding();
    }
  }

  // Add parcels to this carton
  private navigateToAddParcels(): void {
    if (!this.order) return;
    
    const cartonId = this.order.cartonNo || this.order.carrierTrackingNumber;
    if (!cartonId) {
      this.errorMessage = "Cannot identify the carton for update.";
      return;
    }
      // Store target carton information
    this.repackingStateService.setTargetCartonInfo({
      cartonNo: cartonId,
      shopeeOrderNo: this.order?.shopeeOrderNo,
      serviceCode: this.order?.serviceCode ?? undefined,
      destinationRegion: this.order?.destinationRegion ?? undefined,
      status: this.order?.status
    });
    
    // Navigate to add parcels component with correct path - updated to match route definition
    this.router.navigate(['/dashboard/repack/add-parcels', cartonId]);
  }

  // Show modal to select parcels to unbind
  private showParcelSelectionForUnbinding(): void {
    if (!this.order || !this.order.parcels || this.order.parcels.length === 0) {
      this.errorMessage = "This carton has no parcels to unbind.";
      return;
    }
    
    // TODO: Replace with ng-zorro-antd modal when available
    console.log('Showing parcel selection for unbinding:', this.order.parcels.length, 'parcels available');
    const confirmed = confirm(`Select parcels to unbind from ${this.order.parcels.length} available parcels. Proceed?`);
    if (confirmed && this.selectedParcelsForUnbind.length > 0) {
      this.unbindAndTransferParcels();
    }
  }

  // Toggle selection status of a parcel
  toggleParcelSelection(parcel: ParcelDto): void {
    const idx = this.selectedParcelsForUnbind.findIndex(p => p.trackingNumber === parcel.trackingNumber);
    if (idx !== -1) {
      this.selectedParcelsForUnbind.splice(idx, 1);
    } else {
      this.selectedParcelsForUnbind.push(parcel);
    }
  }

  // Check if a parcel is selected
  isParcelSelected(parcel: ParcelDto): boolean {
    return this.selectedParcelsForUnbind.some(p => p.trackingNumber === parcel.trackingNumber);
  }

  // Select all parcels for unbinding
  selectAllParcels(): void {
    if (this.order?.parcels) {
      this.selectedParcelsForUnbind = [...this.order.parcels];
    }
  }

  // Clear all selected parcels
  clearSelectedParcels(): void {
    this.selectedParcelsForUnbind = [];
  }

  // Handle select all parcels checkbox change event
  onSelectAllParcelsChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.checked) {
      this.selectAllParcels();
    } else {
      this.clearSelectedParcels();
    }
  }

  // Process the unbinding and transfer of selected parcels
  private unbindAndTransferParcels(): void {
    if (!this.order || this.selectedParcelsForUnbind.length === 0) return;
    
    const cartonId = this.order.cartonNo || this.order.carrierTrackingNumber || '';
    const shopeeNo = this.order.shopeeOrderNo;
    
    this.isLoading = true;
    this.errorMessage = null;
      // Cancel carton with intent to move parcels to another carton
    const cancelRequestDto: RepackCancelRequestDto = {
      repackedCartonNo: cartonId,
      ilhShopeeNo: shopeeNo,
      destinationRegion: this.order?.destinationRegion || '',
      reasonCode: '1', // Default reason code
      parcelAction: FrontendRepackCancelParcelAction.MoveToExistingCarton // Move to existing carton
    };
    
    this.orderService.repackCancel(cancelRequestDto).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.successMessage = `Carton cancelled. Selected parcels will be transferred to another carton.`;
        
        // Store only the selected parcels for repack
        const pendingState: PendingRepackState = {          parcelsForRepack: this.selectedParcelsForUnbind,
          sourceOrderUniqueId: this.order?.uniqueId || this.orderId!,
          cancelledCartonIlhShopeeNo: shopeeNo,
          cartonConfigToReuse: {
            serviceCode: this.order?.serviceCode,
            destinationRegion: this.order?.destinationRegion || ''
          }
        };
        console.log("Setting PendingRepackState for transfer:", JSON.stringify(pendingState, null, 2));
        this.repackingStateService.setPendingRepackState(pendingState);
        
        // Set the pending rebind state to force completing the rebind
        this.repackingStateService.setPendingRebindState(this.selectedParcelsForUnbind);
        
        // Navigate to add-to-existing component
        setTimeout(() => {
          this.navigateToRepackAddToExisting();
        }, 1000);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        if (err.error && err.error.message) {
          this.errorMessage = `Failed to cancel carton: ${err.error.message}`;
        } else if (err.message) {
          this.errorMessage = `Failed to cancel carton: ${err.message}`;
        } else {
          this.errorMessage = 'An unknown error occurred during carton cancellation.';
        }
        console.error('Repack cancel error:', err);
      }
    });
  }

  // Add this method to check if repack operations are valid for the current order
  canPerformRepack(): boolean {
    if (!this.order) return false;
    
    // Check if essential fields are available and status is not Cancelled
    const hasCartonIdentifier = !!this.order.cartonNo || !!this.order.carrierTrackingNumber;
    //const hasValidStatus = this.order.status !== 'Cancelled';
    
    return hasCartonIdentifier; //;
  }

  // Placeholder for Repack Cancel

  // Repack Cancel implementation - using the modal template approach
  initiateRepackCancel(): void {
    if (!this.order) {
      this.errorMessage = "Order details not loaded.";
      return;
    }
    
    // TODO: Replace with ng-zorro-antd modal when available
    console.log('Repack cancel initiated for order:', this.order.shopeeOrderNo);
    const confirmed = confirm('Are you sure you want to cancel this repack?');
    if (confirmed) {
      this.performRepackCancel();
    }
  }

  private performRepackCancel(): void {
      if (!this.order) return;
      
      const repackedCartonIdentifier = this.order.cartonNo || this.order.carrierTrackingNumber;
      if (!repackedCartonIdentifier) {
        this.errorMessage = "Cannot identify the repacked carton number for cancellation.";
        return;
      }
      
      this.isLoading = true;
      this.errorMessage = null;
      this.successMessage = null;      const request: RepackCancelRequestDto = {
        repackedCartonNo: repackedCartonIdentifier,
        ilhShopeeNo: this.order?.shopeeOrderNo,
        destinationRegion: this.order?.destinationRegion || '',
        reasonCode: this.repackCancelReasonCode.toString(),
        parcelAction: FrontendRepackCancelParcelAction.MoveToExistingCarton
      };

      this.orderService.repackCancel(request).subscribe({
        next: (response) => {
          this.isLoading = false;
          this.successMessage = response.message || 'Repack cancellation request submitted successfully.';          // Store data for add to existing
          const pendingState: PendingRepackState = {
            parcelsForRepack: this.order?.parcels || [],
            sourceOrderUniqueId: this.order?.uniqueId || this.orderId!,
            cancelledCartonIlhShopeeNo: this.order?.shopeeOrderNo || '',
            cartonConfigToReuse: {
              serviceCode: this.order?.serviceCode,
              destinationRegion: this.order?.destinationRegion || ''
            }
          };
          
          this.repackingStateService.setPendingRepackState(pendingState);
          
          // Navigate to orders dashboard
          setTimeout(() => {
            this.router.navigate(['/dashboard/orders']);
          }, 1000);
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Repack cancel error:', err);
          this.errorMessage = `Failed to cancel repack: ${err.error?.message || err.message || 'Unknown server error'}`;
        }
      });
    }
  //async initiateRepackCancel(): Promise<void> {
  //  if (!this.order) {
  //    this.errorMessage = "Order details not loaded.";
  //    return;
  //  }
  //  // Use shopeeOrderNo as the primary identifier for the carton being cancelled if available,
  //  // otherwise fall back to cartonNo or carrierTrackingNumber.
  //  const cartonIdentifierForDisplay = this.order.shopeeOrderNo || this.order.cartonNo || this.order.carrierTrackingNumber;
  //  const cartonIdForApi = this.order.cartonNo || this.order.carrierTrackingNumber; // Actual carton_no for API
  //  const shopeeNoForApi = this.order.shopeeOrderNo; // ILH Shopee No for API

  //  if (!cartonIdForApi && !shopeeNoForApi) {
  //    this.errorMessage = "Cannot identify the repacked carton for cancellation (missing carton_no and ILH Shopee No).";
  //    return;
  //  }

  //  const modalRef = this.modalService.open(RepackCancelModalComponent, {
  //    centered: true,
  //    backdrop: 'static' // Prevents closing on backdrop click
  //  });

  //  // Pass data to the modal
  //  modalRef.componentInstance.repackedCartonIdentifier = cartonIdentifierForDisplay;
  //  modalRef.componentInstance.shopeeOrderNo = this.order.shopeeOrderNo;
  //  modalRef.componentInstance.parcels = [...(this.order.parcels || [])];

  //   try {
  //    const result: RepackCancelChoice = await modalRef.result;
  //    // Ensure cartonIdForApi and shopeeNoForApi are passed correctly.
  //    // If cartonNo is the primary ID for cancellation API, use it.
  //    // If shopeeOrderNo is primary, use that. The DTO expects both.
  //    this.handleRepackCancelChoice(result, cartonIdForApi || '', shopeeNoForApi || '', [...(this.order.parcels || [])]);
  //  } catch (reason) {
  //    console.log('Modal dismissed:', reason);
  //    if (reason && typeof reason === 'object' && 'action' in reason && (reason as RepackCancelChoice).action === 'abort') {
  //      this.successMessage = 'Repack cancellation aborted.';
  //    } else if (reason === ModalDismissReasons.ESC) {
  //      this.successMessage = 'Repack cancellation process closed via ESC key.';
  //    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
  //      this.successMessage = 'Repack cancellation process closed by clicking backdrop.';
  //    } else {
  //      this.successMessage = 'Repack cancellation process was closed.';
  //    }
  //   this.errorMessage = null; // Clear any previous error messages
  //  }
  //}
  private handleRepackCancelChoice(choice: RepackCancelChoice, cartonId: string, shopeeNo: string, originalParcels: ParcelDto[]): void {
    this.isLoading = true;
    this.errorMessage = null;
    
    // Use success message from modal if provided
    if (choice.successMessage) {
      this.successMessage = choice.successMessage;
    } else {
      this.successMessage = null;
    }

    if (!this.order || !this.order.destinationRegion) {
      this.errorMessage = 'Cannot proceed: Critical order or carton information (destination region) is missing.';
      this.isLoading = false; 
      return;
    }

    if (choice.action === 'abort') {
        this.successMessage = 'Repack cancellation aborted.';
        this.isLoading = false;
        return;
    }

    if (!choice.reasonCode || choice.reasonCode.trim() === '') {
        this.errorMessage = 'A non-empty reason code for cancellation is required.';
        this.isLoading = false;
        return;
    }

    // Create a reusable carton configuration with proper null checks
    const cartonConfigToReuse = {
      destinationRegion: this.order?.destinationRegion || '',
      // Include all physical dimensions for reference
      cartonWeight: this.order?.cartonWeight,
      cartonLength: this.order?.cartonLength,
      cartonWidth: this.order?.cartonWidth,
      cartonHeight: this.order?.cartonHeight,
      cartonVolume: this.order?.cartonVolume,
      goodsType: this.order?.goodsType !== undefined ? Number(this.order.goodsType) : undefined,
      transportType: this.order?.transportType !== undefined ? Number(this.order.transportType) : undefined,
      serviceCode: this.order?.serviceCode,
    };

    const cancelRequestDto: RepackCancelRequestDto = {
      repackedCartonNo: cartonId,
      ilhShopeeNo: shopeeNo,
      destinationRegion: this.order?.destinationRegion || '',
      reasonCode: choice.reasonCode.trim(),
      parcelAction: FrontendRepackCancelParcelAction.UnbindOnly
    };

    this.orderService.repackCancel(cancelRequestDto).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        // Show success message with appropriate styling
        this.successMessage = `Carton ${cartonId} cancelled successfully. ${response?.message || ''}`;
        this.errorMessage = null;

        if (choice.action === 'justCancel') {
          this.loadOrderDetails(); // Reload to reflect changes
        } else {
          // Prepare state for navigation based on action
          const pendingState: PendingRepackState = {
            parcelsForRepack: originalParcels,
            sourceOrderUniqueId: this.order?.uniqueId || this.orderId!,
            cancelledCartonIlhShopeeNo: shopeeNo,
            senderToUse: this.order?.Sender ? { ...this.order.Sender } : undefined,
            receiverToUse: this.order?.Receiver ? { ...this.order.Receiver } : undefined,
            preCarrierToUse: (this.order as any)?.preCarrierinfo ? 
              (Array.isArray((this.order as any).preCarrierinfo) ? 
                { ...((this.order as any).preCarrierinfo[0]) } : 
                { ...((this.order as any).preCarrierinfo) }) : null,
            nextCarrierToUse: (this.order as any)?.nextCarrierinfo ? 
              (Array.isArray((this.order as any).nextCarrierinfo) ?
                { ...((this.order as any).nextCarrierinfo[0]) } :
                { ...((this.order as any).nextCarrierinfo) }) : null,
            cartonConfigToReuse: cartonConfigToReuse
          };
          
          this.repackingStateService.setPendingRepackState(pendingState);
          
          if (choice.action === 'cancelAndCreate') {
            this.successMessage += " Now, proceed to create a new repack.";
            this.router.navigate(['/dashboard/repack/create']);
          } else if (choice.action === 'cancelAndAddToExisting') {
            this.successMessage += " Now, proceed to add the unbound parcels to an existing carton.";
            this.router.navigate(['/dashboard/repack-add-to-existing']);
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        
        // Check if error message actually indicates success
        if (err.error?.message && (
            err.error.message.includes('success') || 
            err.error.message.toLowerCase().includes('cancelled') ||
            err.error.message.toLowerCase().includes('cancellation'))) {
          
          this.successMessage = `Carton ${cartonId} cancelled successfully. ${err.error.message}`;
          this.errorMessage = null;
          
          // Handle navigation based on action
          if (choice.action === 'justCancel') {
            this.loadOrderDetails(); // Reload to reflect changes
          } else {
            // Prepare state for navigation based on action
            const pendingState: PendingRepackState = {
              parcelsForRepack: originalParcels,
              sourceOrderUniqueId: this.order?.uniqueId || this.orderId!,
              cancelledCartonIlhShopeeNo: shopeeNo,
              senderToUse: this.order?.Sender ? { ...this.order.Sender } : undefined,
              receiverToUse: this.order?.Receiver ? { ...this.order.Receiver } : undefined,
              preCarrierToUse: (this.order as any)?.preCarrierinfo ? 
                (Array.isArray((this.order as any).preCarrierinfo) ? 
                  { ...((this.order as any).preCarrierinfo[0]) } : 
                  { ...((this.order as any).preCarrierinfo) }) : null,
              nextCarrierToUse: (this.order as any)?.nextCarrierinfo ? 
                (Array.isArray((this.order as any).nextCarrierinfo) ?
                  { ...((this.order as any).nextCarrierinfo[0]) } :
                  { ...((this.order as any).nextCarrierinfo) }) : null,
              cartonConfigToReuse: cartonConfigToReuse
            };
            
            this.repackingStateService.setPendingRepackState(pendingState);
            
            if (choice.action === 'cancelAndCreate') {
              this.successMessage += " Now, proceed to create a new repack.";
              this.router.navigate(['/dashboard/repack/create']);
            } else if (choice.action === 'cancelAndAddToExisting') {
              this.successMessage += " Now, proceed to add the unbound parcels to an existing carton.";
              this.router.navigate(['/dashboard/repack-add-to-existing']);
            }
          }
          
          return;
        }
        
        // Regular error handling
        if (err.error && err.error.message) {
          this.errorMessage = `Failed to cancel carton: ${err.error.message}`;
        } else if (err.message) {
          this.errorMessage = `Failed to cancel carton: ${err.message}`;
        } else {
          this.errorMessage = 'An unknown error occurred during carton cancellation.';
        }
        this.successMessage = null;
        console.error('Repack cancel error:', err);
      }
    });
  }

   // Placeholder methods for follow-up actions
  private startRepackCreateWithUnboundParcels(parcels: ParcelDto[]): void {
    console.log('Navigating to create repack with parcels:', parcels.map(p => p.trackingNumber));
    // Path is now relative to the dashboard
    this.router.navigate(['/dashboard/repack/create'], { 
      state: { 
        parcelsToRepack: parcels,
        originalShopeeOrderNo: this.order?.shopeeOrderNo,
        originalOrderId: this.order?.uniqueId 
      } 
    });
  }

  private startAddUnboundParcelsToExisting(parcels: ParcelDto[]): void {
    console.log('Navigating to add to existing repack with parcels:', parcels.map(p => p.trackingNumber));
    if (!this.order) {
      this.errorMessage = "Original order details are missing, cannot proceed.";
      return;
    }
    const navigationExtras: NavigationExtras = {
      state: {
        parcelsToAdd: parcels,
        originalOrderId: this.order.uniqueId, // Pass the uniqueId of the order being viewed
        originalShopeeOrderNo: this.order.shopeeOrderNo // Pass the shopeeOrderNo of the order being viewed
        
      }
    };
    // Assuming your route for RepackAddToExistingComponent is '/dashboard/repack-add-to-existing'
    this.router.navigate(['/dashboard/repack-add-to-existing'], navigationExtras);
  }

  private handleApiError(err: HttpErrorResponse, contextMessage: string): void {
    this.isLoading = false;
    console.error(`${contextMessage}:`, err);
    this.errorMessage = `${contextMessage}: ${err.error?.message || err.message || 'Unknown server error'}`;
    if (err.status === 404 && contextMessage.includes('load order details')) {
        this.errorMessage = `Order with ID ${this.orderId} not found.`;
    }
    this.successMessage = null;
  }

  // Placeholder for updating order status
  updateStatus(newStatus: string): void {
    if (!this.order || !this.orderId) return;
    console.log(`Update status for order ${this.orderId} to ${newStatus}`);
    // Call service: this.orderService.updateOrderStatus(this.orderId, { status: newStatus }).subscribe(...);
    alert('Update status UI not yet implemented.');
  }

  // Navigate to paths with fixed routes
  private navigateToRepackCreate(): void {
    this.router.navigate(['/dashboard/repack/create']);
  }
  
  private navigateToRepackAddToExisting(): void {
    this.router.navigate(['/dashboard/repack-add-to-existing']);
  }
  
  // Helper method to update pending state and navigate with delay
  private updateStateAndNavigate(pendingState: PendingRepackState, navigateFn: () => void, delay: number = 1000): void {
    console.log(`Setting PendingRepackState:`, JSON.stringify(pendingState, null, 2));
    this.repackingStateService.setPendingRepackState(pendingState);

    setTimeout(() => {
      navigateFn();
    }, delay);
  }

  // === DS TOKEN HELPERS ===

  getOrderStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default' {
    switch (status) {
      case 'Completed': case 'Delivered': return 'success';
      case 'OrderReceived': return 'default';
      case 'InfoReceived': case 'Arrived': return 'info';
      case 'ManifestPending': case 'LoadingDataReady': case 'CustomsProcessing': return 'warning';
      case 'Cancelled': case 'Error': return 'danger';
      default: return 'neutral';
    }
  }

  // Customs Clearance modal methods
  openCustomsClearanceModal(): void {
    if (!this.order || !this.order.parcels || this.order.parcels.length === 0) {
      this.errorMessage = "No parcels available for customs clearance update.";
      return;
    }

    // Reset selections
    this.selectedParcelsForCC = [];
    this.filteredParcelsForCC = [...this.order.parcels];
    this.parcelFilterText = '';

    // Open Bootstrap modal using native Bootstrap 5 API
    const modalElement = document.getElementById('customsClearanceModal');
    if (modalElement) {
      // @ts-ignore - Bootstrap is loaded globally
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  proceedToCustomsClearance(): void {
    // Close the modal
    const modalElement = document.getElementById('customsClearanceModal');
    if (modalElement) {
      // @ts-ignore - Bootstrap is loaded globally
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Navigate with selected parcels
    this.navigateToCustomsClearanceWithParcels(this.selectedParcelsForCC);
  }

  filterParcels(): void {
    if (!this.order || !this.order.parcels) return;

    if (!this.parcelFilterText || this.parcelFilterText.trim() === '') {
      this.filteredParcelsForCC = [...this.order.parcels];
    } else {
      const filterLower = this.parcelFilterText.toLowerCase();
      this.filteredParcelsForCC = this.order.parcels.filter(p =>
        p.trackingNumber.toLowerCase().includes(filterLower)
      );
    }
  }

  toggleParcelForCC(parcel: ParcelDto): void {
    const idx = this.selectedParcelsForCC.findIndex(p => p.trackingNumber === parcel.trackingNumber);
    if (idx !== -1) {
      this.selectedParcelsForCC.splice(idx, 1);
    } else {
      this.selectedParcelsForCC.push(parcel);
    }
  }

  isParcelSelectedForCC(parcel: ParcelDto): boolean {
    return this.selectedParcelsForCC.some(p => p.trackingNumber === parcel.trackingNumber);
  }

  toggleSelectAllParcelsForCC(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.checked) {
      this.selectedParcelsForCC = [...this.filteredParcelsForCC];
    } else {
      this.selectedParcelsForCC = [];
    }
  }

  private navigateToCustomsClearanceWithParcels(parcels: ParcelDto[]): void {
    const trackingNumbers = parcels.map(p => p.trackingNumber);

    this.router.navigate(['/dashboard/customs-clearance-progress'], {
      state: {
        trackingNumbers: trackingNumbers,
        cartonNo: this.order?.cartonNo || this.order?.carrierTrackingNumber,
        source: 'carton-detail'
      }
    });
  }
}



